const concatUntilEmpty = arr => {
  if (!arr) return '';
  let end = arr.findIndex(el => !el);
  if (end < 0) end = arr.length;
  return arr.slice(0, end).join('');
};

/**
 * Programmatically copies a text string to the clipboard. Attempts to copy in both secure and non-secure contexts as well as VSCode.
 *
 * Accepts a container element. This helps ensure the text can get copied to the clipboard correctly in non-secure
 * environments, the container should be active (such as a button in a modal) to ensure the content can be copied.
 *
 * @param {String} text - Text to copy
 * @param {HTMLElement} el - element that emits event for VSCode, and contains the fallback textarea.
 */
const copyToClipboard = (textToCopy, el) => {
  // Fist, emit a custom event for VS Code extensions and other environments
  // This bubbles up to parent components that handle clipboard operations
  el.dispatchEvent(new CustomEvent('copy-code-snippet', {
    bubbles: true,
    cancelable: true,
    detail: {
      code: textToCopy
    }
  }));

  // Second, for modern browsers try a simple clipboard.writeText (works on https and localhost)
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(textToCopy);
  }

  // Third, try execCommand to copy from a dynamically created invisible textarea (for http and older browsers)
  const textarea = document.createElement('textarea');
  textarea.value = textToCopy;
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px'; // eslint-disable-line @gitlab/require-i18n-strings
  textarea.style.top = '0';
  textarea.setAttribute('readonly', ''); // prevent keyboard popup on mobile

  // textarea must be in document to be selectable, but we add it to the button so it works in modals
  el.appendChild(textarea);
  textarea.select(); // for Safari
  textarea.setSelectionRange(0, textarea.value.length); // for mobile devices

  try {
    const done = document.execCommand('copy');
    el.removeChild(textarea);
    return done ? Promise.resolve() : Promise.reject(new Error('Copy command failed'));
  } catch (err) {
    el.removeChild(textarea);
    return Promise.reject(err);
  }
};

export { concatUntilEmpty, copyToClipboard };
